// Helpers for entry-bound cart products (products whose source product had
// `validWith === 'entry'`, e.g. engraved medal insert). The backend rejects such
// a product in the /delivery and /transaction payload when it does not carry the
// `entryId` of the related registration (422, `product` validation error), so the
// cart has to keep that link correct even when the reservation expires and the
// registration is recreated with a fresh entryId.

const sameId = (a, b) => a != null && b != null && Number(a) === Number(b)

// Flatten single + team member entries into a plain list of entries that have an entryId.
const flattenEntries = entries =>
  (Array.isArray(entries) ? entries : []).reduce((acc, entry) => {
    if (Array.isArray(entry.memberEntries)) {
      return acc.concat(entry.memberEntries)
    }
    if (entry.entryId != null) {
      acc.push(entry)
    }
    return acc
  }, [])

// A cart product is bound to a registration when its source product had
// `validWith === 'entry'`. Carts persisted before `validWith` / `pendingEntry`
// were stored on the cart item are recognised by the participant fields that are
// only set for entry-bound products.
export const isEntryBoundProduct = product =>
  !!product &&
  (product.validWith === 'entry' || product.pendingEntry === true || product.participantId != null)

// Cart entries an entry-bound product could be attached to: same event and –
// when the participant is known – same participant.
export const findCartEntriesForProduct = (entries, product) =>
  flattenEntries(entries).filter(entry => {
    const sameEvent = sameId(entry.race && entry.race.eventId, product.eventId)
    const sameParticipant = product.participantId == null || sameId(entry.uid, product.participantId)
    return sameEvent && sameParticipant
  })

// Resolve the entryId of an entry-bound product from the current cart entries.
// Returns an id only when exactly one entry matches; otherwise null (ambiguous or
// no match → the user has to pick one explicitly).
export const resolveEntryIdForProduct = (entries, product) => {
  if (product.entryId) return product.entryId
  const matches = findCartEntriesForProduct(entries, product)
  return matches.length === 1 ? matches[0].entryId : null
}

// Whether a given entryId still belongs to an entry that is present in the cart.
export const cartEntryExists = (entries, entryId) =>
  flattenEntries(entries).some(entry => sameId(entry.entryId, entryId))
